import { Component, computed, input, output, signal } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidSyllabicKeyboard } from '../rid-syllabic-keyboard/rid-syllabic-keyboard';
import {
  keyboardGroups,
  RidSyllabicKeyboardUtils,
  TKey,
  TKeyboardLayout,
  TKeyValue,
} from '../rid-syllabic-keyboard/rid-syllabic-keyboard.utils';

export type TRule =
  | { name: 'DUBBELZETTER' }
  | { name: 'KORTSTE_VORM' }
  | { name: 'LANGER_MAKEN' }
  | { name: 'MEDEKLINKER'; klank: Extract<TKey, { type: 'KLANK' }>['value'] }
  | { name: 'TEKENDIEF' }
  | { name: 'VRAAGTEKEN' };

function getRuleFromKey(key: TKey): TRule {
  if (key.type !== 'RULE') throw new Error(`Key is not a rule: ${JSON.stringify(key)}`);

  if (key.value === '+■') {
    throw new Error(`Cannot get rule from key with value '+■', missing klank information`);
  }

  return {
    name: ({ '+': 'DUBBELZETTER', '-': 'TEKENDIEF', '←': 'KORTSTE_VORM', '→': 'LANGER_MAKEN', '?': 'VRAAGTEKEN' } as const)[key.value],
  };
}

@Component({
  selector: 'rid-functional-syllabic-keyboard',
  imports: [RidSyllabicKeyboard],
  templateUrl: './rid-functional-syllabic-keyboard.html',
})
export class RidFunctionalSyllabicKeyboard {
  layout = input<TKeyboardLayout | undefined>();
  validate = input<(key: TKey) => boolean>();
  mask = input<TKeyValue[] | undefined>(undefined);
  mode = input<'GROUP' | 'KLANK' | 'BOTH'>('BOTH');
  correctValue = input<TKeyValue>();

  content = signal<TKey[]>([]);
  activeRule = signal<TRule | undefined>(undefined);
  waiting = signal(false);
  attempts = signal(0);
  postfix = computed<TKey[]>(() => {
    const activeRule = this.activeRule();
    if (!activeRule) return [];

    switch (activeRule.name) {
      case 'DUBBELZETTER':
        return [{ type: 'RULE', value: '+' }];
      case 'KORTSTE_VORM':
        return [{ type: 'RULE', value: '←' }];
      case 'LANGER_MAKEN':
        return [{ type: 'RULE', value: '→' }];
      case 'TEKENDIEF':
        return [{ type: 'RULE', value: '-' }];
      case 'MEDEKLINKER':
        return [{ type: 'RULE', value: '+■' }];
      case 'VRAAGTEKEN':
        return [{ type: 'RULE', value: '?' }];
      default:
        return [];
    }
  });
  hintMask = computed<TKeyValue[] | undefined>(() => {
    const activeRule = this.activeRule();

    const correctValue = this.correctValue();

    if (activeRule) {
      switch (activeRule.name) {
        case 'DUBBELZETTER':
          return [...keyboardGroups['MEDEKLINKERS'].map((k) => k.value)];
        case 'TEKENDIEF':
          return [...keyboardGroups['KORTE_LANGE_KLINKERS'].map((k) => k.value)];
        case 'MEDEKLINKER': {
          const lastChar = this.content().at(-1);
          return lastChar ? [lastChar.value] : [...keyboardGroups['MEDEKLINKERS'].map((k) => k.value)];
        }
        case 'LANGER_MAKEN': {
          const groups: TKeyValue[][] = [
            ['b', 'p'],
            ['d', 't'],
          ];
          return correctValue ? (groups.find((group) => group.includes(correctValue)) ?? groups.flat()) : groups.flat();
        }
        case 'KORTSTE_VORM': {
          const groups: TKeyValue[][] = [
            ['au', 'auw', 'ou', 'ouw'],
            ['ch', 'g'],
            ['ng', 'nk'],
          ];
          return correctValue ? (groups.find((group) => group.includes(correctValue)) ?? groups.flat()) : groups.flat();
        }
      }
    }

    return undefined;
  });
  showHint = computed(() => this.attempts() >= 2);
  finalMask = computed(() => RidSyllabicKeyboardUtils.combineMasks(this.showHint() ? this.hintMask() : undefined, this.mask()));
  isEmpty = computed(() => this.content().length === 0 && !this.activeRule());

  incorrected = output<void>();
  inserted = output<TKey>();
  appliedRule = output<TRule>();

  protected transform(key: TKey): TKey {
    const correctValue = this.correctValue();
    if (correctValue && key.type === 'GROUP') {
      const correctKey = RidSyllabicKeyboardUtils.convertValueToKey(correctValue);
      const areSameGroup = RidSyllabicKeyboardUtils.getKeyGroupName(correctKey) === RidSyllabicKeyboardUtils.getKeyGroupName(key);
      if (areSameGroup) return correctKey;
    }

    return key;
  }

  press(key: TKey) {
    if (this.waiting()) return;

    key = this.transform(key);

    const validate = this.validate();
    if (validate && !validate(key)) {
      this.pressInvalid(key);
      return;
    }

    const activeRule = this.activeRule();
    if (activeRule) {
      switch (activeRule.name) {
        case 'DUBBELZETTER': {
          if (
            key.type !== 'KLANK' ||
            !['b', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm', 'n', 'p', 'r', 's', 't', 'v', 'w', 'z'].includes(key.value)
          ) {
            this.pressInvalid(key);
            return;
          }

          this.push(key);
          this.applyRule({ name: 'MEDEKLINKER', klank: key.value });
          return;
        }
        case 'KORTSTE_VORM': {
          if (key.type !== 'KLANK' || !['au', 'ou', 'ch', 'ng', 'nk', 'auw', 'ouw', 'g'].includes(key.value)) {
            this.pressInvalid(key);
            return;
          }

          this.push(key);
          return;
        }
        case 'LANGER_MAKEN': {
          if (key.type !== 'KLANK' || !['b', 'd', 'p', 't'].includes(key.value)) {
            this.pressInvalid(key);
            return;
          }

          this.push(key);
          return;
        }
        case 'TEKENDIEF': {
          if (key.type !== 'KLANK' || !key.value.startsWith('(')) {
            this.pressInvalid(key);
            return;
          }

          this.push(key);
          return;
        }
        case 'MEDEKLINKER': {
          if (key.type !== 'KLANK' || key.value !== activeRule.klank) {
            this.pressInvalid(key);
            return;
          }

          this.push(key);
          return;
        }
        case 'VRAAGTEKEN': {
          if (key.type !== 'RULE') {
            this.pressInvalid(key);
            return;
          }

          this.applyRule(getRuleFromKey(key));
          return;
        }
        default: {
          activeRule satisfies never;
          console.error('Unknown active rule', activeRule);
          throw new Error(`Unhandled active rule`);
        }
      }
    }

    if (key.type === 'RULE') {
      this.applyRule(getRuleFromKey(key));
      return;
    }

    this.push(key);
  }

  private async pressInvalid(key: TKey) {
    this.waiting.set(true);

    const activeRule = this.activeRule();
    this.content.update((current) => [...current, key]);
    this.activeRule.set(undefined);
    this.incorrected.emit();
    await delay(500);
    this.pop();
    this.activeRule.set(activeRule);

    this.waiting.set(false);

    this.attempts.update((a) => a + 1);
  }

  private applyRule(rule: TRule) {
    this.appliedRule.emit(rule);
    this.activeRule.set(rule);
  }

  push(key: TKey) {
    this.content.update((current) => [...current, key]);
    this.activeRule.set(undefined);
    this.inserted.emit(key);
    this.attempts.set(0);
  }

  pop() {
    if (this.activeRule()) {
      this.activeRule.set(undefined);
      return;
    }

    this.content.update((current) => current.slice(0, -1));
  }

  clear() {
    this.content.set([]);
    this.activeRule.set(undefined);
    this.attempts.set(0);
  }
}
